import { FC, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { getMetadata } from 'video-metadata-thumbnails';
import { Alert } from '@mui/material';
import { acceptableMediaTypes, IMAGE_SIZE_MB, VIDEO_SIZE_MB } from './contants';
import { generatePreviewURL } from './helper';
import { UploadView } from './UploadView';
import { useTranslation } from 'react-i18next';
import { ProductType } from '@/models/ad';

type Props = {
  onUpload: (file: File) => void;
  onPreview: (url: string) => void;
  acceptType: keyof typeof acceptableMediaTypes;
  productType: ProductType;
  preview?: string;
};

export const UploadArea: FC<Props> = ({
  onUpload,
  onPreview,
  acceptType,
  productType,
  preview,
}) => {
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const { t } = useTranslation();

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptType === 'video') {
      const mb = parseFloat((acceptedFiles[0].size / 1000 / 1000).toFixed(2));

      if (mb > VIDEO_SIZE_MB) {
        setToastMsg(t('upload_area.max_video_size_limitation'));
        return;
      }

      const { duration } = await getMetadata(acceptedFiles[0]);

      if (productType === ProductType.Video15s && Math.floor(duration) > 15) {
        setToastMsg(t('upload_area.max_15s_video_length_limitation'));
        return;
      } else if (
        productType === ProductType.Video30s &&
        Math.floor(duration) > 30
      ) {
        setToastMsg(t('upload_area.max_30s_video_length_limitation'));
        return;
      }
    }

    if (acceptType === 'image') {
      const mb = parseFloat((acceptedFiles[0].size / 1000 / 1000).toFixed(2));
      if (mb > IMAGE_SIZE_MB) {
        setToastMsg(t('upload_area.max_image_size_limitation'));
        return;
      }
    }

    const url = await generatePreviewURL(acceptedFiles[0]);
    if (url) onPreview(url);
    onUpload(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      ...acceptableMediaTypes[acceptType],
    },
    maxFiles: 1,
    onDrop,
  });

  return (
    <section className="relative h-full w-full min-w-64 min-h-24 cursor-pointer">
      {toastMsg && (
        <Alert
          variant="filled"
          severity="error"
          onClose={() => setToastMsg('')}
        >
          {toastMsg}
        </Alert>
      )}
      <div
        {...getRootProps({
          style: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            outline: 'none',
            height: '100%',
          },
        })}
      >
        <input {...getInputProps()} />
        {!preview && <UploadView />}
      </div>
    </section>
  );
};
